import { LabelType, LabelItem } from "./labelTypes"
import { parseQuestionLabel, buildQuestionLabel } from "./questionLabelHelpers"

/**
 * Updates all occurrences of a specific label or list in the questionnaire content
 *
 * @param questionnaireContent - The original questionnaire content
 * @param labelItem - The label item with updated content
 * @returns The updated questionnaire content with all occurrences replaced
 */
export const updateLabelInQuestionnaire = (
  questionnaireContent: string,
  labelItem: LabelItem
): string => {
  const lines = questionnaireContent.split("\n")

  switch (labelItem.type) {
    case LabelType.QUESTION: {
      // For question labels we update the label name AND the question wording.
      const oldName = labelItem.name

      // Bail on malformed content — it must be in "Q <name>: ..." form.
      if (!/^Q\s+(.+?):/.test(labelItem.content)) {
        return questionnaireContent
      }

      const { label: newName, text: newText } = parseQuestionLabel(
        labelItem.content
      )

      if (!newName) {
        return questionnaireContent // Invalid format, return unchanged
      }

      const nameChanged = oldName !== newName

      // Replace the question line (rebuilding name + wording) and rename any
      // <<Old Name>> references (IF statements, PER loops, piping) when renamed.
      const updatedLines = lines.map((line) => {
        const questionMatch = line.match(/^Q\s+(.+?):(.*)/)
        if (questionMatch && questionMatch[1].trim() === oldName) {
          return buildQuestionLabel(newName, newText)
        }

        if (nameChanged && line.includes(`<<${oldName}>>`)) {
          return line.replace(
            new RegExp(
              `<<${oldName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}>>`,
              "g"
            ),
            `<<${newName}>>`
          )
        }

        return line
      })

      return updatedLines.join("\n")
    }

    case LabelType.RANGE_LIST: {
      // For range lists, replace the entire range list block AND all references
      const oldName = labelItem.name
      const newContentLines = labelItem.content.split("\n")
      const newRangeMatch = newContentLines[0].match(
        /^RANGE\s+LIST\s+(.+?):\s*(.+)/
      )

      if (!newRangeMatch) {
        return questionnaireContent
      }

      const newName = newRangeMatch[1].trim()

      let i = 0
      const updatedLines: string[] = []

      while (i < lines.length) {
        const line = lines[i]
        const rangeMatch = line.match(/^RANGE\s+LIST\s+(.+?):\s*(.+)/)

        if (rangeMatch && rangeMatch[1].trim() === oldName) {
          // Found a range list with the old name, replace the entire block
          updatedLines.push(...newContentLines)

          // Skip the original range list items
          i++
          while (i < lines.length) {
            const currentLine = lines[i]
            const currentTrimmed = currentLine.trim()

            // Check if this is a scale item
            if (currentTrimmed && /^\d+:/.test(currentTrimmed)) {
              i++
              continue
            }

            // Stop if we hit another definition or empty line
            if (
              !currentTrimmed ||
              currentTrimmed.startsWith("Q ") ||
              currentTrimmed.startsWith("LIST ") ||
              currentTrimmed.startsWith("RANGE LIST ") ||
              currentTrimmed.startsWith("RANDOMIZE ") ||
              currentTrimmed.startsWith("NOTE ") ||
              currentTrimmed.startsWith("TEXT ") ||
              currentTrimmed === "EXCEPTIONS"
            ) {
              break
            }
            i++
          }
        } else {
          // Check if this line references the old range list name
          let updatedLine = line

          // Replace SELECT ONE FROM <OldName> with SELECT ONE FROM <NewName>
          updatedLine = updatedLine.replace(
            new RegExp(`\\bFROM\\s+${oldName}\\b`, "gi"),
            `FROM ${newName}`
          )

          updatedLines.push(updatedLine)
          i++
        }
      }

      return updatedLines.join("\n")
    }

    case LabelType.LIST: {
      // For regular lists, we need to replace the entire list block AND all references
      const oldName = labelItem.name
      const newContentLines = labelItem.content.split("\n")
      const newListHeaderMatch = newContentLines[0].match(/^LIST\s+(.+)/)

      if (!newListHeaderMatch) {
        return questionnaireContent
      }

      const newName = newListHeaderMatch[1].trim()

      // Find and replace all list blocks with the old name
      let i = 0
      const updatedLines: string[] = []

      while (i < lines.length) {
        const line = lines[i]
        const listMatch = line.match(/^LIST\s+(.+)/)

        if (listMatch && listMatch[1].trim() === oldName) {
          // Found a list with the old name, replace the entire block
          updatedLines.push(...newContentLines)

          // Skip the original list items
          i++
          while (i < lines.length) {
            const currentLine = lines[i]
            const currentTrimmed = currentLine.trim()

            if (
              !currentTrimmed ||
              currentTrimmed.startsWith("Q ") ||
              currentTrimmed.startsWith("LIST ") ||
              currentTrimmed.startsWith("RANGE LIST ") ||
              currentTrimmed.startsWith("RANDOMIZE ") ||
              currentTrimmed.startsWith("NOTE ") ||
              currentTrimmed.startsWith("TEXT ") ||
              currentTrimmed === "EXCEPTIONS"
            ) {
              break
            }
            i++
          }
        } else {
          // Check if this line references the old list name
          let updatedLine = line

          // Replace PER <OldName> with PER <NewName>
          updatedLine = updatedLine.replace(
            new RegExp(`\\bPER\\s+${oldName}\\b`, "gi"),
            `PER ${newName}`
          )

          // Replace FROM <OldName> with FROM <NewName>
          updatedLine = updatedLine.replace(
            new RegExp(`\\bFROM\\s+${oldName}\\b`, "gi"),
            `FROM ${newName}`
          )

          // Replace RANDOMIZE <OldName> with RANDOMIZE <NewName>
          updatedLine = updatedLine.replace(
            new RegExp(`\\bRANDOMIZE\\s+${oldName}\\b`, "gi"),
            `RANDOMIZE ${newName}`
          )

          updatedLines.push(updatedLine)
          i++
        }
      }

      return updatedLines.join("\n")
    }

    case LabelType.EXCEPTIONS: {
      // For exceptions, replace the entire exceptions block
      const newContentLines = labelItem.content.split("\n")

      let i = 0
      const updatedLines: string[] = []

      while (i < lines.length) {
        const line = lines[i]
        const trimmedLine = line.trim()

        if (trimmedLine === "EXCEPTIONS" || trimmedLine === "LIST EXCEPTIONS") {
          // Found an exceptions block, replace it
          updatedLines.push(...newContentLines)

          // Skip the original exception items
          i++
          while (i < lines.length) {
            const currentLine = lines[i]
            const currentTrimmed = currentLine.trim()

            if (
              !currentTrimmed ||
              currentTrimmed.startsWith("Q ") ||
              currentTrimmed.startsWith("LIST ") ||
              currentTrimmed.startsWith("RANGE LIST ") ||
              currentTrimmed.startsWith("RANDOMIZE ") ||
              currentTrimmed.startsWith("NOTE ") ||
              currentTrimmed.startsWith("TEXT ") ||
              currentTrimmed === "EXCEPTIONS"
            ) {
              break
            }
            i++
          }
        } else {
          updatedLines.push(line)
          i++
        }
      }

      return updatedLines.join("\n")
    }

    default:
      return questionnaireContent
  }
}

/**
 * Updates multiple labels/lists in the questionnaire content
 *
 * @param questionnaireContent - The original questionnaire content
 * @param labelItems - Array of label items with updated content
 * @returns The updated questionnaire content with all changes applied
 */
export const updateMultipleLabels = (
  questionnaireContent: string,
  labelItems: LabelItem[]
): string => {
  let updatedContent = questionnaireContent

  // Process each label item
  for (const labelItem of labelItems) {
    updatedContent = updateLabelInQuestionnaire(updatedContent, labelItem)
  }

  return updatedContent
}
